// Programmatic-SEO engine: unique keyword × location landing pages.
// URL: /{kw}-{loc} (default DE) · /en/{kw}-{loc} · /fr/{kw}-{loc} · /it/{kw}-{loc}
// Hub: /{kw} (+ language prefixes). Content varies by keyword + city + language
// -> no duplicate pages/links.
import { SEO_LOCATIONS } from "@/data/seoLocations";
import { VILLAGE_LOCATIONS } from "@/data/villageLocations";
import { SEO_I18N } from "@/data/seoI18n";

export { SEO_LOCATIONS, SEO_I18N };
export const BASE_LANGS = ["de", "en", "fr", "it"];
export const SEO_LANGS = ["de", "en", "fr", "it", "es", "pt", "ko", "zh", "ja", "ar", "ckb", "kmr", "fa", "he", "el", "hi"];
export const RTL_LANGS = ["ar", "ckb", "fa", "he"];
// Fare cities (with cityId) + village/town content pages (no fare) — deduped, fare wins.
const _fareSlugs = new Set(SEO_LOCATIONS.map((l) => l.slug));
export const ALL_LOCATIONS = [
  ...SEO_LOCATIONS,
  ...VILLAGE_LOCATIONS.filter((v) => !_fareSlugs.has(v.slug)),
];

// Ordered most-specific first so slug parsing matches greedily ("taxi" last).
export const SEO_KEYWORDS = [
  {
    slug: "airport-transfer", angle: "transfer",
    label: { de: "Airport Transfer", en: "Airport Transfer", fr: "Transfert aéroport", it: "Transfer aeroporto" },
    lead: {
      de: (c) => `Privater Airport Transfer vom Flughafen Zürich (ZRH) nach ${c} zum garantierten Festpreis – Ihr professioneller Chauffeur empfängt Sie in der Ankunftshalle.`,
      en: (c) => `Private airport transfer from Zürich Airport (ZRH) to ${c} at a guaranteed fixed fare – your professional chauffeur greets you in the arrivals hall.`,
      fr: (c) => `Transfert aéroport privé depuis l'aéroport de Zurich (ZRH) vers ${c} à prix fixe garanti – votre chauffeur professionnel vous accueille dans le hall des arrivées.`,
      it: (c) => `Transfer aeroporto privato dall'aeroporto di Zurigo (ZRH) a ${c} a tariffa fissa garantita – il vostro chauffeur professionale vi accoglie nell'area arrivi.`,
    },
  },
  {
    slug: "airport-taxi", angle: "taxi",
    label: { de: "Airport Taxi", en: "Airport Taxi", fr: "Taxi aéroport", it: "Taxi aeroporto" },
    lead: {
      de: (c) => `Zuverlässiges Airport Taxi vom Flughafen Zürich nach ${c} – ohne Taxameter, mit Fixpreis, Flugüberwachung und Gratis-Wartezeit.`,
      en: (c) => `Reliable airport taxi from Zürich Airport to ${c} – no taxi meter, a fixed price, flight monitoring and free waiting time.`,
      fr: (c) => `Taxi aéroport fiable depuis l'aéroport de Zurich vers ${c} – sans taximètre, à prix fixe, avec suivi des vols et temps d'attente gratuit.`,
      it: (c) => `Taxi aeroporto affidabile dall'aeroporto di Zurigo a ${c} – senza tassametro, a prezzo fisso, con monitoraggio dei voli e attesa gratuita.`,
    },
  },
  {
    slug: "flughafentransfer", angle: "transfer",
    label: { de: "Flughafentransfer", en: "Airport transfer", fr: "Transfert aéroport", it: "Transfer aeroporto" },
    lead: {
      de: (c) => `Ihr Flughafentransfer Zürich – ${c}: komfortabel, pünktlich und zum transparenten Festpreis. Moderne Fahrzeuge und diskrete Chauffeure.`,
      en: (c) => `Your Zürich airport transfer to ${c}: comfortable, punctual and at a transparent fixed fare. Modern vehicles and discreet chauffeurs.`,
      fr: (c) => `Votre transfert aéroport Zurich – ${c} : confortable, ponctuel et à prix fixe transparent. Véhicules modernes et chauffeurs discrets.`,
      it: (c) => `Il vostro transfer aeroporto Zurigo – ${c}: comodo, puntuale e a tariffa fissa trasparente. Veicoli moderni e chauffeur discreti.`,
    },
  },
  {
    slug: "flughafentaxi", angle: "taxi",
    label: { de: "Flughafentaxi", en: "Airport taxi", fr: "Taxi aéroport", it: "Taxi aeroporto" },
    lead: {
      de: (c) => `Flughafentaxi Zürich nach ${c} – 24/7 verfügbar, Festpreis ohne versteckte Kosten und Empfang direkt am Terminal.`,
      en: (c) => `Zürich airport taxi to ${c} – available 24/7, a fixed price with no hidden costs and a meet & greet right at the terminal.`,
      fr: (c) => `Taxi aéroport Zurich vers ${c} – disponible 24h/24, prix fixe sans frais cachés et accueil directement au terminal.`,
      it: (c) => `Taxi aeroporto Zurigo verso ${c} – disponibile 24 ore su 24, prezzo fisso senza costi nascosti e accoglienza direttamente al terminal.`,
    },
  },
  {
    slug: "limousine-service", angle: "limousine",
    label: { de: "Limousinenservice", en: "Limousine service", fr: "Service de limousine", it: "Servizio limousine" },
    lead: {
      de: (c) => `Exklusiver Limousinenservice von Zürich nach ${c}: First- und Business-Class-Fahrzeuge für Geschäftsreisen, Events und besondere Anlässe.`,
      en: (c) => `Exclusive limousine service from Zürich to ${c}: first- and business-class vehicles for business trips, events and special occasions.`,
      fr: (c) => `Service de limousine exclusif de Zurich vers ${c} : véhicules first et business class pour voyages d'affaires, événements et occasions spéciales.`,
      it: (c) => `Servizio limousine esclusivo da Zurigo a ${c}: veicoli first e business class per viaggi d'affari, eventi e occasioni speciali.`,
    },
  },
  {
    slug: "chauffeur-service", angle: "chauffeur",
    label: { de: "Chauffeurservice", en: "Chauffeur service", fr: "Service de chauffeur", it: "Servizio chauffeur" },
    lead: {
      de: (c) => `Persönlicher Chauffeurservice für die Strecke Zürich – ${c}. Ortskundige, professionelle Fahrer – auf Wunsch stundenweise buchbar.`,
      en: (c) => `Personal chauffeur service for the Zürich – ${c} route. Local, professional drivers – also bookable by the hour on request.`,
      fr: (c) => `Service de chauffeur personnel pour le trajet Zurich – ${c}. Chauffeurs professionnels connaissant la région – réservable à l'heure sur demande.`,
      it: (c) => `Servizio chauffeur personale per la tratta Zurigo – ${c}. Autisti professionali del posto – prenotabile anche a ore su richiesta.`,
    },
  },
  {
    slug: "event-transfer", angle: "event",
    label: { de: "Event- & Kongresstransfer", en: "Event & congress transfer", fr: "Transfert événements & congrès", it: "Transfer eventi & congressi" },
    lead: {
      de: (c) => `Event- und Kongresstransfer Zürich – ${c}: zuverlässige Gruppentransfers für Messen, Kongresse, Hochzeiten und Firmenanlässe, koordiniert und pünktlich.`,
      en: (c) => `Event and congress transfer Zürich – ${c}: reliable group transfers for trade fairs, congresses, weddings and corporate occasions, coordinated and on time.`,
      fr: (c) => `Transfert événements et congrès Zurich – ${c} : transferts de groupe fiables pour salons, congrès, mariages et événements d'entreprise, coordonnés et ponctuels.`,
      it: (c) => `Transfer eventi e congressi Zurigo – ${c}: transfer di gruppo affidabili per fiere, congressi, matrimoni ed eventi aziendali, coordinati e puntuali.`,
    },
  },
  {
    slug: "business-transfer", angle: "business",
    label: { de: "Business Transfer", en: "Business transfer", fr: "Transfert affaires", it: "Transfer business" },
    lead: {
      de: (c) => `Business Transfer Zürich – ${c} für anspruchsvolle Geschäftsreisende: Pünktlichkeit, Diskretion und Komfort in der Business- oder First-Class-Klasse.`,
      en: (c) => `Business transfer Zürich – ${c} for demanding corporate travellers: punctuality, discretion and comfort in business- or first-class vehicles.`,
      fr: (c) => `Transfert affaires Zurich – ${c} pour voyageurs d'affaires exigeants : ponctualité, discrétion et confort en classe business ou first.`,
      it: (c) => `Transfer business Zurigo – ${c} per viaggiatori d'affari esigenti: puntualità, discrezione e comfort in classe business o first.`,
    },
  },
  {
    slug: "taxi", angle: "taxi",
    label: { de: "Taxi", en: "Taxi", fr: "Taxi", it: "Taxi" },
    lead: {
      de: (c) => `Taxi Zürich – ${c} zum Festpreis: bequeme Online-Buchung, saubere Fahrzeuge und faire, transparente Preise ohne Taxameter.`,
      en: (c) => `Taxi from Zürich to ${c} at a fixed price: easy online booking, clean vehicles and fair, transparent pricing with no meter.`,
      fr: (c) => `Taxi Zurich – ${c} à prix fixe : réservation en ligne facile, véhicules propres et tarifs justes et transparents sans taximètre.`,
      it: (c) => `Taxi Zurigo – ${c} a prezzo fisso: prenotazione online facile, veicoli puliti e tariffe eque e trasparenti senza tassametro.`,
    },
  },
];

const _bySlug = Object.fromEntries(ALL_LOCATIONS.map((l) => [l.slug, l]));
export const keywordBySlug = (slug) => SEO_KEYWORDS.find((k) => k.slug === slug);
export const locationBySlug = (slug) => _bySlug[slug];

// Language-aware label/lead: base langs use inline templates, others use AI SEO_I18N.
export const kwLabel = (kw, lang) =>
  BASE_LANGS.includes(lang) ? (kw.label[lang] || kw.label.de) : (SEO_I18N[lang]?.labels?.[kw.slug] || kw.label.en);
export const kwLead = (kw, lang, c) => {
  if (BASE_LANGS.includes(lang)) return (kw.lead[lang] || kw.lead.de)(c);
  const t = SEO_I18N[lang]?.leads?.[kw.slug];
  return t ? t.split("{c}").join(c) : kw.lead.en(c);
};

// Resolve a single-segment slug into a hub page or a keyword+location page.
export function resolveSeo(seoSlug) {
  if (!seoSlug) return null;
  const kwHub = keywordBySlug(seoSlug);
  if (kwHub) return { type: "hub", kw: kwHub };
  for (const kw of SEO_KEYWORDS) {
    const prefix = kw.slug + "-";
    if (seoSlug.startsWith(prefix)) {
      const loc = locationBySlug(seoSlug.slice(prefix.length));
      if (loc) return { type: "city", kw, loc };
    }
  }
  return null;
}
